import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Package, Flag, Star, Plus, Trash2, Ban, CheckCircle, X, Edit3, AlertTriangle, Download, Megaphone, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { uploadToCloudinary } from '@/utils/cloudinary';

const emptyGame = { name: '', banner_url: '', filters: [] };
const emptyFilter = { name: '', type: 'select', options: '' };

const emptyLinksConfig = {
  website_links: [],
  middleman_services: [],
  discord_servers: [],
};

const emptyLinkItem = { label: '', url: '', badge: '' };

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('games');
  const [stats, setStats] = useState({});
  const [games, setGames] = useState([]);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [announcement, setAnnouncement] = useState({ enabled: false, text_before: '', link_text: '', link_url: '', text_after: '' });
  const [annLoading, setAnnLoading] = useState(false);
  const [linksConfig, setLinksConfig] = useState(emptyLinksConfig);
  const [linksLoading, setLinksLoading] = useState(false);
  const [listings, setListings] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [gameModal, setGameModal] = useState(false);
  const [gameForm, setGameForm] = useState(emptyGame);
  const [editGameId, setEditGameId] = useState(null);
  const [filterForm, setFilterForm] = useState(emptyFilter);
  const [banModal, setBanModal] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [userDetailModal, setUserDetailModal] = useState(null);
  const [userDetailSaving, setUserDetailSaving] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [editListingModal, setEditListingModal] = useState(false);
  const [editListingData, setEditListingData] = useState(null);
  const [editListingLoading, setEditListingLoading] = useState(false);
  const [adminImgLoading, setAdminImgLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    const [st, g, r, u, rv, l, fb, ann, lnk] = await Promise.allSettled([
      API.get('/admin/stats'), API.get('/games'),
      API.get('/admin/reports'), API.get('/admin/users'),
      API.get('/admin/reviews'), API.get('/admin/listings'),
      API.get('/admin/feedback'), API.get('/announcement'),
      API.get('/links-config'),
    ]);
    if (st.status === 'fulfilled') setStats(st.value.data);
    if (g.status === 'fulfilled') setGames(g.value.data);
    if (r.status === 'fulfilled') setReports(r.value.data);
    if (u.status === 'fulfilled') setUsers(u.value.data);
    if (rv.status === 'fulfilled') setReviews(rv.value.data);
    if (l.status === 'fulfilled') setListings(l.value.data);
    if (fb.status === 'fulfilled') setFeedbackList(fb.value.data);
    if (ann.status === 'fulfilled') setAnnouncement(ann.value.data);
    if (lnk.status === 'fulfilled') setLinksConfig({
      website_links: lnk.value.data?.website_links || [],
      middleman_services: lnk.value.data?.middleman_services || [],
      discord_servers: lnk.value.data?.discord_servers || [],
    });
  };

  const openCreateGame = () => { setGameForm(emptyGame); setEditGameId(null); setGameModal(true); };
  const openEditGame = (g) => {
    setGameForm({ name: g.name, banner_url: g.banner_url || '', filters: g.filters.map(f => ({ ...f, options: f.options?.join(', ') || '' })) });
    setEditGameId(g.id); setGameModal(true);
  };

  const addFilter = () => setGameForm(f => ({ ...f, filters: [...f.filters, { ...emptyFilter }] }));
  const removeFilter = (i) => setGameForm(f => ({ ...f, filters: f.filters.filter((_, idx) => idx !== i) }));
  const updateFilter = (i, field, val) => {
    const updated = [...gameForm.filters];
    updated[i] = { ...updated[i], [field]: val };
    setGameForm(f => ({ ...f, filters: updated }));
  };

  const handleGameBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgLoading(true);
    try {
      const url = await uploadToCloudinary(file, 'games');
      setGameForm(f => ({ ...f, banner_url: url }));
    } catch { toast.error('Upload failed'); }
    finally { setImgLoading(false); }
  };

  const handleSaveGame = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: gameForm.name,
        banner_url: gameForm.banner_url,
        filters: gameForm.filters.map(f => ({
          name: f.name, type: f.type,
          options: f.type === 'select' ? f.options.split(',').map(o => o.trim()).filter(Boolean) : undefined
        }))
      };
      if (editGameId) {
        await API.put(`/games/${editGameId}`, payload);
        toast.success('Game updated!');
      } else {
        await API.post('/games', payload);
        toast.success('Game added!');
      }
      setGameModal(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save game');
    } finally { setLoading(false); }
  };

  const handleDeleteGame = async (id) => {
    if (!window.confirm('Remove this game?')) return;
    await API.delete(`/games/${id}`);
    setGames(prev => prev.filter(g => g.id !== id));
    toast.success('Game removed');
  };

  const handleImportGames = async () => {
    setImportLoading(true);
    try {
      const { data } = await API.post('/admin/import-games');
      toast.success(`Imported ${data.imported} new games! (${data.skipped} already existed)`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImportLoading(false); }
  };

  const handleBanUser = async () => {
    await API.put(`/admin/users/${banModal.id}/ban`, { action: 'ban', reason: banReason });
    toast.success(`User ${banModal.username} banned`);
    setBanModal(null); setBanReason('');
    loadAll();
  };

  const handleUnban = async (id) => {
    await API.put(`/admin/users/${id}/unban`, {});
    toast.success('User unbanned');
    loadAll();
  };

  const openUserDetail = (u) => {
    setUserDetailModal({
      ...u,
      contacts: (u.contact_methods || []).map(c => ({ ...c })),
      editRole: u.role,
    });
  };

  const handleSaveUserDetail = async () => {
    if (!userDetailModal) return;
    setUserDetailSaving(true);
    try {
      await API.put(`/admin/users/${userDetailModal.id}`, {
        role: userDetailModal.editRole,
        contact_methods: userDetailModal.contacts.filter(c => c.value?.trim()),
      });
      toast.success('User updated!');
      setUserDetailModal(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally { setUserDetailSaving(false); }
  };

  const updateModalContact = (type, val) => {
    setUserDetailModal(prev => {
      const contacts = [...(prev.contacts || [])];
      const idx = contacts.findIndex(c => c.type === type);
      if (idx >= 0) {
        if (!val.trim()) contacts.splice(idx, 1);
        else contacts[idx] = { type, value: val };
      } else if (val.trim()) {
        contacts.push({ type, value: val });
      }
      return { ...prev, contacts };
    });
  };

  const handleDeleteReview = async (id) => {
    await API.delete(`/reviews/${id}`);
    setReviews(prev => prev.filter(r => r.id !== id));
    toast.success('Review deleted');
  };

  const handleDeleteListing = async (id) => {
    try {
      await API.delete(`/admin/listings/${id}`);
      setListings(prev => prev.filter(l => l.id !== id));
      toast.success('Listing deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete listing');
    }
  };

  const openEditListing = (l) => {
    setEditListingData({ id: l.id, title: l.title, description: l.description || '', price: String(l.price), quantity: String(l.quantity), is_active: l.is_active, image_urls: l.image_urls || [] });
    setEditListingModal(true);
  };

  const handleEditListingSubmit = async (e) => {
    e.preventDefault();
    if (editListingLoading) return;
    setEditListingLoading(true);
    try {
      await API.put(`/listings/${editListingData.id}`, {
        title: editListingData.title,
        description: editListingData.description,
        price: parseFloat(editListingData.price),
        quantity: parseInt(editListingData.quantity),
        is_active: editListingData.is_active,
        image_urls: editListingData.image_urls || [],
        image_url: (editListingData.image_urls || [])[0] || '',
      });
      toast.success('Listing updated!');
      setEditListingModal(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update listing');
    } finally { setEditListingLoading(false); }
  };

  // ─── Admin Edit Listing Image Helpers ────────────────────────────────────
  const adminRemoveImage = (index) => {
    setEditListingData(d => ({ ...d, image_urls: d.image_urls.filter((_, i) => i !== index) }));
  };

  const adminHandleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAdminImgLoading(true);
    try {
      const url = await uploadToCloudinary(file);
      setEditListingData(d => ({ ...d, image_urls: [...(d.image_urls || []), url] }));
    } catch { toast.error('Image upload failed'); }
    finally { setAdminImgLoading(false); }
  };

  const adminHandlePasteUrl = () => {
    const url = prompt('Paste image URL:');
    if (url && url.startsWith('http')) {
      setEditListingData(d => ({ ...d, image_urls: [...(d.image_urls || []), url] }));
    }
  };

  // ─── Links Config Helpers ────────────────────────────────────────────────
  const addLinkItem = (section) => {
    setLinksConfig(prev => ({
      ...prev,
      [section]: [...prev[section], { id: Date.now().toString(), label: '', url: '', badge: '' }]
    }));
  };

  const removeLinkItem = (section, id) => {
    setLinksConfig(prev => ({
      ...prev,
      [section]: prev[section].filter(item => item.id !== id)
    }));
  };

  const updateLinkItem = (section, id, field, value) => {
    setLinksConfig(prev => ({
      ...prev,
      [section]: prev[section].map(item => item.id === id ? { ...item, [field]: value } : item)
    }));
  };

  const handleSaveLinks = async () => {
    setLinksLoading(true);
    try {
      const payload = {
        website_links: (linksConfig.website_links || []).map(i => ({ ...i, badge: i.badge || null })),
        middleman_services: (linksConfig.middleman_services || []).map(i => ({ ...i, badge: i.badge || null })),
        discord_servers: (linksConfig.discord_servers || []).map(i => ({ ...i, badge: i.badge || null })),
      };
      await API.put('/admin/links-config', payload);
      toast.success('Links saved!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save links'); }
    finally { setLinksLoading(false); }
  };

  const handleReportAction = async (rid, status, action) => {
    await API.put(`/admin/reports/${rid}`, { status, admin_action: action });
    setReports(prev => prev.map(r => r.id === rid ? { ...r, status, admin_action: action } : r));
    toast.success('Report updated');
  };

  const TABS = [
    { id: 'games', label: 'Games', icon: Package },
    { id: 'reports', label: `Reports ${stats.pending_reports ? `(${stats.pending_reports})` : ''}`, icon: Flag },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'listings', label: 'Listings', icon: Package },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <div className="flex items-center gap-3 mb-8">
        <Shield size={28} className="text-rm-red" />
        <h1 className="font-rajdhani font-bold text-3xl uppercase">Admin <span className="text-rm-red">Panel</span></h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
        {[
          ['Total Users', stats.total_users],
          ['Sellers', stats.total_sellers],
          ['Listings', stats.total_listings],
          ['Reviews', stats.total_reviews],
          ['Reports', stats.pending_reports],
          ['Games', stats.total_games],
          ['Reveals', stats.total_reveals],
        ].map(([label, val]) => (
          <div key={label} className="stat-card text-center">
            <p className="font-rajdhani font-bold text-2xl text-white">{val ?? 0}</p>
            <p className="text-[9px] text-zinc-600 uppercase tracking-[0.12em] mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap border-b border-rm-border mb-6 gap-0">
        {[
          { id: 'games', label: 'Games', icon: Package },
          { id: 'reports', label: `Reports ${stats.pending_reports ? `(${stats.pending_reports})` : ''}`, icon: Flag },
          { id: 'users', label: 'Users', icon: Users },
          { id: 'reviews', label: 'Reviews', icon: Star },
          { id: 'listings', label: 'Listings', icon: Package },
          { id: 'feedback', label: `Feedback ${feedbackList.filter(f=>f.status==='open').length ? `(${feedbackList.filter(f=>f.status==='open').length})` : ''}`, icon: Flag },
          { id: 'announcement', label: 'Announcement', icon: Megaphone },
          { id: 'links', label: 'Links', icon: Link2 },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-5 py-3 font-rajdhani font-bold uppercase tracking-wide text-sm transition-colors
              ${tab === id ? 'text-rm-red border-b-2 border-rm-red -mb-[2px]' : 'text-rm-muted hover:text-white'}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* GAMES TAB */}
      {tab === 'games' && (
        <div>
          <div className="flex justify-end gap-3 mb-4">
            <button onClick={handleImportGames} disabled={importLoading} data-testid="import-games-btn"
              className="btn-outline px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 disabled:opacity-50">
              <Download size={15} /> {importLoading ? 'Importing...' : 'Import Game Library'}
            </button>
            <button onClick={openCreateGame} data-testid="add-game-btn"
              className="btn-primary px-5 py-2.5 rounded-sm text-sm flex items-center gap-2">
              <Plus size={15} /> Add Game
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {games.map(g => (
              <div key={g.id} className="bg-rm-card border border-rm-border rounded-sm overflow-hidden card-hover" data-testid={`admin-game-${g.id}`}>
                {g.banner_url && <img src={g.banner_url} alt={g.name} className="w-full aspect-video object-cover" />}
                <div className="p-4">
                  <h3 className="font-rajdhani font-bold uppercase text-white">{g.name}</h3>
                  <p className="text-xs text-rm-muted mt-1">{g.filters?.length || 0} custom filters</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => openEditGame(g)} className="flex-1 btn-outline py-1.5 text-xs rounded-sm flex items-center justify-center gap-1" data-testid={`edit-game-${g.id}`}>
                      <Edit3 size={12} /> Edit
                    </button>
                    <button onClick={() => handleDeleteGame(g.id)} className="flex-1 btn-outline py-1.5 text-xs rounded-sm text-rm-red border-rm-red/30 hover:bg-rm-red/10 flex items-center justify-center gap-1" data-testid={`delete-game-${g.id}`}>
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* REPORTS TAB */}
      {tab === 'reports' && (
        <div className="space-y-3">
          {reports.length === 0 ? <p className="text-rm-muted text-center py-8">No reports</p> : reports.map(r => (
            <div key={r.id} className="bg-rm-card border border-rm-border rounded-sm p-4" data-testid={`report-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${r.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}>
                      {r.status}
                    </span>
                    <span className="text-xs text-rm-muted">{r.reason?.replace('_', ' ')}</span>
                  </div>
                  <p className="text-sm text-white font-medium">Reported: {r.seller_username}</p>
                  {r.listing_title && <p className="text-xs text-rm-muted">Listing: {r.listing_title}</p>}
                  <p className="text-xs text-rm-muted mt-1">By: {r.reporter_username}</p>
                  <p className="text-xs text-rm-muted mt-1">{r.description}</p>
                  {r.proof_image_url && <img src={r.proof_image_url} alt="Proof" className="mt-2 w-32 h-20 object-cover rounded-sm border border-rm-border" />}
                </div>
                {r.status === 'pending' && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <button onClick={() => handleReportAction(r.id, 'resolved', 'warn')}
                      className="px-3 py-1.5 text-xs border border-yellow-500/40 text-yellow-500 hover:bg-yellow-500/10 rounded-sm font-bold uppercase">
                      Warn Seller
                    </button>
                    <button onClick={() => { setBanModal(users.find(u => u.id === r.seller_id) || { id: r.seller_id, username: r.seller_username }); }}
                      className="px-3 py-1.5 text-xs border border-rm-red/40 text-rm-red hover:bg-rm-red/10 rounded-sm font-bold uppercase">
                      Ban Seller
                    </button>
                    <button onClick={() => handleReportAction(r.id, 'resolved', null)}
                      className="px-3 py-1.5 text-xs border border-rm-border text-rm-muted hover:text-white rounded-sm font-bold uppercase">
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* USERS TAB */}
      {tab === 'users' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rm-border">
                {['Username', 'Email', 'Role', 'Verified', 'Status', 'Actions'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-widest text-rm-muted font-rajdhani">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.filter(u => u.role !== 'admin').map(u => (
                <tr key={u.id} className="border-b border-rm-border/50 hover:bg-rm-surface/50" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4 font-medium text-white">{u.username}</td>
                  <td className="py-3 px-4 text-rm-muted text-xs">{u.email}</td>
                  <td className="py-3 px-4"><span className="text-xs border border-rm-border px-2 py-0.5 uppercase font-bold text-rm-muted">{u.role}</span></td>
                  <td className="py-3 px-4">{u.email_verified ? <CheckCircle size={14} className="text-green-500" /> : <X size={14} className="text-rm-muted" />}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-bold uppercase ${u.is_banned ? 'text-rm-red' : 'text-green-500'}`}>
                      {u.is_banned ? 'Banned' : 'Active'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openUserDetail(u)} data-testid={`view-user-${u.id}`}
                        className="text-xs text-zinc-400 hover:text-white border border-rm-border/50 hover:border-white/30 px-2 py-1 rounded-sm transition-colors flex items-center gap-1">
                        <Edit3 size={11} /> Details
                      </button>
                      {u.is_banned ? (
                        <button onClick={() => handleUnban(u.id)} data-testid={`unban-${u.id}`}
                          className="text-xs text-green-500 hover:underline">Unban</button>
                      ) : (
                        <button onClick={() => setBanModal(u)} data-testid={`ban-${u.id}`}
                          className="text-xs text-rm-red hover:underline flex items-center gap-1">
                          <Ban size={11} /> Ban
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* REVIEWS TAB */}
      {tab === 'reviews' && (
        <div className="space-y-3">
          {reviews.filter(r => !r.is_deleted).map(r => (
            <div key={r.id} className="bg-rm-card border border-rm-border rounded-sm p-4 flex items-start gap-4" data-testid={`admin-review-${r.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-bold ${r.rating_type === 'positive' ? 'text-green-500' : 'text-rm-red'}`}>{r.rating_type}</span>
                  <span className="text-xs text-rm-muted">by {r.buyer_username}</span>
                  <span className="text-xs text-rm-muted">for {r.seller_id}</span>
                </div>
                <p className="text-sm text-rm-muted">{r.text}</p>
                {r.proof_image_url && <img src={r.proof_image_url} alt="Proof" className="mt-2 w-24 h-16 object-cover rounded-sm border border-rm-border" />}
              </div>
              <button onClick={() => handleDeleteReview(r.id)} data-testid={`delete-review-${r.id}`}
                className="text-rm-muted hover:text-rm-red p-1 transition-colors shrink-0">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {reviews.filter(r => !r.is_deleted).length === 0 && <p className="text-rm-muted text-center py-8">No reviews</p>}
        </div>
      )}

      {/* LISTINGS TAB */}
      {tab === 'listings' && (
        <div className="space-y-2">
          {listings.map(l => (
            <div key={l.id} className="bg-rm-card border border-rm-border rounded-sm p-3 flex items-center gap-3" data-testid={`admin-listing-${l.id}`}>
              {l.image_url && <img src={l.image_url} alt={l.title} className="w-10 h-10 object-cover rounded-sm" />}
              <div className="flex-1 min-w-0">
                <p className="font-rajdhani font-bold text-sm text-white uppercase truncate">{l.title}</p>
                <p className="text-xs text-rm-muted">{l.game_name} · {l.seller_username} · ${l.price}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-bold uppercase ${l.is_active ? 'text-green-500 bg-green-500/10' : 'text-zinc-600 bg-zinc-700/20'}`}>
                {l.is_active ? 'Active' : 'Inactive'}
              </span>
              <button onClick={() => openEditListing(l)} className="text-rm-muted hover:text-white p-1 transition-colors" title="Edit listing" data-testid={`admin-edit-listing-${l.id}`}>
                <Edit3 size={14} />
              </button>
              <button onClick={() => handleDeleteListing(l.id)} className="text-rm-muted hover:text-rm-red p-1 transition-colors" data-testid={`admin-del-listing-${l.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {listings.length === 0 && <p className="text-rm-muted text-center py-8">No listings</p>}
        </div>
      )}

      {/* FEEDBACK TAB */}
      {tab === 'feedback' && (
        <div className="space-y-3">
          {feedbackList.length === 0 ? <p className="text-rm-muted text-center py-8">No feedback yet</p> : feedbackList.map(fb => (
            <div key={fb.id} className="bg-rm-card border border-rm-border rounded-sm p-4" data-testid={`feedback-${fb.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase border
                      ${fb.category === 'recommend_game' ? 'border-blue-500/40 text-blue-400'
                        : fb.category === 'report_glitch' ? 'border-rm-red/40 text-rm-red'
                        : fb.category === 'suggest_feature' ? 'border-yellow-500/40 text-yellow-400'
                        : 'border-rm-border text-rm-muted'}`}>
                      {fb.category?.replace('_', ' ')}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${fb.status === 'open' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}>
                      {fb.status}
                    </span>
                  </div>
                  <p className="font-medium text-white text-sm">{fb.title}</p>
                  <p className="text-xs text-rm-muted mt-1">{fb.description}</p>
                  {fb.contact_info && <p className="text-xs text-zinc-500 mt-1">Contact: {fb.contact_info}</p>}
                  <p className="text-xs text-zinc-700 mt-1">by {fb.username || 'Anonymous'}</p>
                </div>
                <button onClick={async () => { await API.put(`/admin/feedback/${fb.id}`, { status: fb.status === 'open' ? 'reviewed' : 'open' }); loadAll(); }}
                  className={`text-xs px-3 py-1.5 border rounded-sm font-bold uppercase transition-colors ${fb.status === 'open' ? 'border-green-500/40 text-green-500 hover:bg-green-500/10' : 'border-rm-border text-rm-muted hover:text-white'}`}>
                  {fb.status === 'open' ? 'Mark Reviewed' : 'Reopen'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ANNOUNCEMENT TAB */}
      {tab === 'announcement' && (
        <div className="max-w-xl">
          <div className="bg-rm-card border border-rm-border rounded-sm p-6 space-y-5" data-testid="announcement-admin">
            <div className="flex items-center justify-between">
              <h3 className="font-rajdhani font-bold text-lg uppercase">Site Announcement Bar</h3>
              <div
                className="flex items-center gap-2 cursor-pointer select-none"
                onClick={() => setAnnouncement(a => ({ ...a, enabled: !a.enabled }))}
                data-testid="announcement-toggle"
              >
                <span className="text-sm text-rm-muted">{announcement.enabled ? 'Enabled' : 'Disabled'}</span>
                <div className={`relative w-11 h-6 rounded-full transition-colors ${announcement.enabled ? 'bg-rm-red' : 'bg-zinc-700'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${announcement.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Text Before Link</label>
              <input type="text" value={announcement.text_before} onChange={e => setAnnouncement(a => ({ ...a, text_before: e.target.value }))}
                placeholder="100$ giveaway going on in discord server"
                className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm" data-testid="ann-text-before" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Link Text</label>
                <input type="text" value={announcement.link_text} onChange={e => setAnnouncement(a => ({ ...a, link_text: e.target.value }))}
                  placeholder="join"
                  className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm" data-testid="ann-link-text" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Link URL</label>
                <input type="url" value={announcement.link_url} onChange={e => setAnnouncement(a => ({ ...a, link_url: e.target.value }))}
                  placeholder="https://discord.gg/..."
                  className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm" data-testid="ann-link-url" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Text After Link</label>
              <input type="text" value={announcement.text_after} onChange={e => setAnnouncement(a => ({ ...a, text_after: e.target.value }))}
                placeholder="to take part"
                className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm" data-testid="ann-text-after" />
            </div>
            {/* Preview */}
            {(announcement.text_before || announcement.link_text) && (
              <div className="bg-rm-red/20 border border-rm-red/30 rounded-sm px-4 py-2 text-sm">
                <span className="text-zinc-300 text-xs uppercase tracking-widest mr-2">Preview:</span>
                <span className="text-white">{announcement.text_before} </span>
                {announcement.link_text && <span className="text-blue-300 underline">{announcement.link_text}</span>}
                {announcement.text_after && <span className="text-white"> {announcement.text_after}</span>}
              </div>
            )}
            <button onClick={async () => {
              setAnnLoading(true);
              try {
                await API.put('/announcement', announcement);
                toast.success('Announcement saved!');
              } catch { toast.error('Failed to save'); }
              finally { setAnnLoading(false); }
            }} disabled={annLoading}
              className="w-full py-2.5 bg-rm-red hover:bg-rm-red-hover text-white font-rajdhani font-bold uppercase tracking-wide rounded-sm transition-colors disabled:opacity-50" data-testid="ann-save-btn">
              {annLoading ? 'Saving...' : 'Save Announcement'}
            </button>
          </div>
        </div>
      )}

      {/* LINKS TAB */}
      {tab === 'links' && (
        <div className="max-w-2xl space-y-6">
          {[
            { key: 'website_links', label: 'Website Links', showBadge: false },
            { key: 'middleman_services', label: 'Middleman Services', showBadge: true },
            { key: 'discord_servers', label: 'Discord Servers', showBadge: false },
          ].map(({ key, label, showBadge }) => (
            <div key={key} className="bg-rm-card border border-rm-border rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-rajdhani font-bold uppercase text-sm tracking-widest text-zinc-400">{label}</h3>
                <button onClick={() => addLinkItem(key)}
                  className="flex items-center gap-1 text-xs text-rm-red hover:text-white transition-colors" data-testid={`add-link-${key}`}>
                  <Plus size={13} /> Add
                </button>
              </div>
              <div className="space-y-3">
                {linksConfig[key]?.length === 0 && (
                  <p className="text-xs text-rm-muted text-center py-2">No items. Click Add to create one.</p>
                )}
                {linksConfig[key]?.map((item) => (
                  <div key={item.id} className="bg-rm-surface border border-rm-border rounded-sm p-3 space-y-2" data-testid={`link-item-${item.id}`}>
                    <div className="flex items-center gap-2">
                      <input
                        type="text" value={item.label}
                        onChange={e => updateLinkItem(key, item.id, 'label', e.target.value)}
                        placeholder="Label (e.g. RobloxMart Store)"
                        className="flex-1 bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-2 py-1.5 rounded-sm text-sm"
                        data-testid={`link-label-${item.id}`}
                      />
                      <button onClick={() => removeLinkItem(key, item.id)}
                        className="text-rm-muted hover:text-rm-red transition-colors shrink-0" data-testid={`remove-link-${item.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <input
                      type="text" value={item.url}
                      onChange={e => updateLinkItem(key, item.id, 'url', e.target.value)}
                      placeholder="URL or 'na' if not available"
                      className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-2 py-1.5 rounded-sm text-sm"
                      data-testid={`link-url-${item.id}`}
                    />
                    {showBadge && (
                      <input
                        type="text" value={item.badge || ''}
                        onChange={e => updateLinkItem(key, item.id, 'badge', e.target.value)}
                        placeholder="Badge (e.g. Free, Paid · Trusted) — leave empty for none"
                        className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-2 py-1.5 rounded-sm text-sm"
                        data-testid={`link-badge-${item.id}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button onClick={handleSaveLinks} disabled={linksLoading}
            className="w-full max-w-2xl py-2.5 bg-rm-red hover:bg-rm-red-hover text-white font-rajdhani font-bold uppercase tracking-wide rounded-sm transition-colors disabled:opacity-50"
            data-testid="save-links-btn">
            {linksLoading ? 'Saving...' : 'Save Links'}
          </button>
        </div>
      )}

      {/* Game Modal */}
      {gameModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-rm-card border border-rm-border rounded-sm w-full max-w-lg my-8 animate-fade-in">
            <div className="flex items-center justify-between p-5 border-b border-rm-border">
              <h3 className="font-rajdhani font-bold text-xl uppercase">{editGameId ? 'Edit Game' : 'Add Game'}</h3>
              <button onClick={() => setGameModal(false)} className="text-rm-muted hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={handleSaveGame} className="p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Game Name *</label>
                <input type="text" value={gameForm.name} onChange={e => setGameForm(f => ({ ...f, name: e.target.value }))} required
                  placeholder="e.g. Murder Mystery 2"
                  className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm" data-testid="game-name-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Banner Image</label>
                {gameForm.banner_url ? (
                  <div className="relative">
                    <img src={gameForm.banner_url} className="w-full aspect-video object-cover rounded-sm border border-rm-border" />
                    <button type="button" onClick={() => setGameForm(f => ({ ...f, banner_url: '' }))}
                      className="absolute top-2 right-2 bg-rm-bg/80 border border-rm-border rounded-sm p-1">
                      <X size={14} className="text-rm-muted" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center border border-dashed border-rm-border rounded-sm p-4 cursor-pointer hover:border-rm-red/50 transition-colors">
                    <input type="file" accept="image/*" className="hidden" onChange={handleGameBanner} />
                    {imgLoading ? <div className="w-5 h-5 border-2 border-rm-red border-t-transparent rounded-full animate-spin" />
                      : <span className="text-xs text-rm-muted">Upload Banner</span>}
                  </label>
                )}
                <div className="mt-2">
                  <input type="text" value={gameForm.banner_url} onChange={e => setGameForm(f => ({ ...f, banner_url: e.target.value }))}
                    placeholder="Or paste image URL..."
                    className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm" />
                </div>
              </div>

              {/* Filters */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-widest text-rm-muted">Custom Filters</label>
                  <button type="button" onClick={addFilter} className="text-xs text-rm-red hover:text-white flex items-center gap-1">
                    <Plus size={12} /> Add Filter
                  </button>
                </div>
                <div className="space-y-3">
                  {gameForm.filters.map((f, i) => (
                    <div key={i} className="bg-rm-surface border border-rm-border rounded-sm p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <input type="text" value={f.name} onChange={e => updateFilter(i, 'name', e.target.value)}
                          placeholder="Filter name (e.g. Item Type)"
                          className="flex-1 bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-2 py-1.5 rounded-sm text-sm" />
                        <select value={f.type} onChange={e => updateFilter(i, 'type', e.target.value)}
                          className="bg-rm-bg border border-rm-border text-white text-sm px-2 py-1.5 rounded-sm w-24">
                          <option value="select">Select</option>
                          <option value="text">Text</option>
                        </select>
                        <button type="button" onClick={() => removeFilter(i)} className="text-rm-muted hover:text-rm-red"><Trash2 size={14} /></button>
                      </div>
                      {f.type === 'select' && (
                        <input type="text" value={f.options} onChange={e => updateFilter(i, 'options', e.target.value)}
                          placeholder="Options (comma-separated): Knife, Gun, Pet"
                          className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-2 py-1.5 rounded-sm text-sm" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setGameModal(false)} className="flex-1 btn-outline py-2.5 rounded-sm text-sm">Cancel</button>
                <button type="submit" disabled={loading} data-testid="save-game-btn"
                  className="flex-1 btn-primary py-2.5 rounded-sm text-sm disabled:opacity-50">
                  {loading ? 'Saving...' : editGameId ? 'Update Game' : 'Add Game'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Listing Modal */}
      {editListingModal && editListingData && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-rm-card border border-rm-border rounded-sm max-w-md w-full animate-fade-in">
            <div className="flex items-center justify-between p-5 border-b border-rm-border">
              <h3 className="font-rajdhani font-bold text-xl uppercase">Edit Listing</h3>
              <button onClick={() => setEditListingModal(false)} className="text-rm-muted hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={handleEditListingSubmit} className="p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Title *</label>
                <input type="text" value={editListingData.title} onChange={e => setEditListingData(d => ({ ...d, title: e.target.value }))} required
                  className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm" data-testid="admin-edit-title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Description</label>
                <textarea value={editListingData.description} onChange={e => setEditListingData(d => ({ ...d, description: e.target.value }))}
                  rows={3} className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm resize-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Images</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(editListingData.image_urls || []).map((url, i) => (
                    <div key={i} className="relative w-16 h-16">
                      <img src={url} alt="" className="w-16 h-16 object-cover rounded-sm border border-rm-border" />
                      <button type="button" onClick={() => adminRemoveImage(i)}
                        className="absolute -top-1 -right-1 bg-rm-red rounded-full w-4 h-4 flex items-center justify-center text-white">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center justify-center gap-1 bg-rm-bg border border-rm-border text-rm-muted hover:text-white px-3 py-2 rounded-sm text-xs cursor-pointer transition-colors">
                    {adminImgLoading ? 'Uploading...' : 'Upload Image'}
                    <input type="file" accept="image/*" className="hidden" onChange={adminHandleImageUpload} disabled={adminImgLoading} />
                  </label>
                  <button type="button" onClick={adminHandlePasteUrl}
                    className="flex-1 bg-rm-bg border border-rm-border text-rm-muted hover:text-white px-3 py-2 rounded-sm text-xs transition-colors">
                    Paste URL
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Price ($) *</label>
                  <input type="number" value={editListingData.price} onChange={e => setEditListingData(d => ({ ...d, price: e.target.value }))} required min="0" step="0.01"
                    className="w-full bg-rm-bg border border-rm-border text-white px-3 py-2 rounded-sm text-sm" data-testid="admin-edit-price" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Quantity *</label>
                  <input type="number" value={editListingData.quantity} onChange={e => setEditListingData(d => ({ ...d, quantity: e.target.value }))} required min="0"
                    className="w-full bg-rm-bg border border-rm-border text-white px-3 py-2 rounded-sm text-sm" data-testid="admin-edit-quantity" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs uppercase tracking-widest text-rm-muted">Status</label>
                <button type="button" onClick={() => setEditListingData(d => ({ ...d, is_active: !d.is_active }))}
                  className={`px-4 py-1.5 rounded-sm text-xs font-bold uppercase border transition-colors ${editListingData.is_active ? 'border-green-500 text-green-500 bg-green-500/10' : 'border-rm-border text-rm-muted'}`}
                  data-testid="admin-edit-status-toggle">
                  {editListingData.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditListingModal(false)} className="flex-1 btn-outline py-2.5 rounded-sm text-sm">Cancel</button>
                <button type="submit" disabled={editListingLoading} data-testid="admin-edit-listing-submit"
                  className="flex-1 btn-primary py-2.5 rounded-sm text-sm disabled:opacity-50">
                  {editListingLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {userDetailModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-rm-card border border-rm-border rounded-sm w-full max-w-lg my-8 animate-fade-in">
            <div className="flex items-center justify-between p-5 border-b border-rm-border">
              <div>
                <h3 className="font-rajdhani font-bold text-xl uppercase">{userDetailModal.username}</h3>
                <p className="text-xs text-rm-muted">{userDetailModal.email}</p>
              </div>
              <button onClick={() => setUserDetailModal(null)} className="text-rm-muted hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-5">
              {/* Role Change */}
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-3">Account Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {['buyer', 'seller'].map(r => (
                    <button key={r} type="button"
                      onClick={() => setUserDetailModal(prev => ({ ...prev, editRole: r }))}
                      data-testid={`admin-role-${r}`}
                      className={`py-2.5 border rounded-sm font-rajdhani font-bold uppercase text-sm transition-all
                        ${userDetailModal.editRole === r
                          ? 'border-rm-red text-rm-red bg-rm-red/10'
                          : 'border-rm-border text-rm-muted hover:border-white/20 hover:text-white'}`}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Methods */}
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-3">Contact Methods</label>
                <div className="space-y-2">
                  {[
                    { type: 'discord',  placeholder: 'YourName#1234 or discord.gg/...' },
                    { type: 'telegram', placeholder: '@YourTelegram' },
                    { type: 'email',    placeholder: 'contact@email.com' },
                    { type: 'website',  placeholder: 'https://yoursite.com' },
                  ].map(({ type, placeholder }) => (
                    <div key={type} className="flex items-center gap-3">
                      <span className="text-xs text-rm-muted capitalize w-20 shrink-0">{type}</span>
                      <input type="text" placeholder={placeholder}
                        value={userDetailModal.contacts?.find(c => c.type === type)?.value || ''}
                        onChange={e => updateModalContact(type, e.target.value)}
                        data-testid={`admin-contact-${type}`}
                        className="flex-1 bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-xs" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setUserDetailModal(null)} className="flex-1 btn-outline py-2.5 rounded-sm text-sm">Cancel</button>
                <button onClick={handleSaveUserDetail} disabled={userDetailSaving}
                  data-testid="save-user-detail-btn"
                  className="flex-1 btn-primary py-2.5 rounded-sm text-sm disabled:opacity-50">
                  {userDetailSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ban Modal */}
      {banModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-rm-card border border-rm-red/30 rounded-sm max-w-sm w-full p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={20} className="text-rm-red" />
              <h3 className="font-rajdhani font-bold text-xl uppercase">Ban {banModal.username}?</h3>
            </div>
            <textarea value={banReason} onChange={e => setBanReason(e.target.value)}
              placeholder="Reason for ban..." rows={3}
              className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm resize-none mb-4" />
            <div className="flex gap-3">
              <button onClick={() => setBanModal(null)} className="flex-1 btn-outline py-2 text-sm rounded-sm">Cancel</button>
              <button onClick={handleBanUser} data-testid="confirm-ban-btn"
                className="flex-1 btn-primary py-2 text-sm rounded-sm">Confirm Ban</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
